/**
 * Modal de criação de usuário — TH-53165.
 *
 * Lista de roles disponíveis depende da role do caller:
 * - super_admin: qualquer role do sistema
 * - tenant_admin: tenant_admin, store_manager, operator
 *
 * Backend devolve `temporary_password` no body — exibimos num
 * sub-modal pra que o admin copie e passe ao usuário (até M1.E5
 * EmailSender entregar o welcome real).
 *
 * Gerente de loja sem loja vê lista vazia (PSHWO-67453): o campo de loja
 * aparece e é obrigatório quando a role é store_manager.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";

const SUPER_ADMIN_ROLES = [
  "super_admin",
  "tenant_admin",
  "store_manager",
  "operator",
  "catalog_operator",
  "salesperson",
] as const;
const TENANT_ADMIN_ROLES = [
  "tenant_admin",
  "store_manager",
  "operator",
] as const;

const schema = z
  .object({
    name: z.string().max(120).optional().or(z.literal("")),
    email: z.string().min(3, "Email obrigatório").max(255),
    role: z.string().min(1, "Role obrigatória"),
    store_id: z.string().optional(),
  })
  .refine((d) => d.role !== "store_manager" || !!d.store_id, {
    message: "Escolha a loja do gerente",
    path: ["store_id"],
  });

type FormData = z.infer<typeof schema>;

interface UserCreated {
  id: string;
  email: string;
  name: string | null;
  role: string;
  temporary_password: string;
}

interface StoreOption {
  id: string;
  name: string;
}

async function fetchStores(): Promise<StoreOption[]> {
  // per_page=100 é o limite do backend; mesmo corte do ImportsPage (TH-55119).
  const res = await fetch("/api/stores?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return ((await res.json()) as { data: StoreOption[] }).data;
}

async function createUser(data: FormData): Promise<UserCreated> {
  const res = await fetch("/api/users", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      email: data.email,
      name: data.name?.trim() ? data.name : null,
      role: data.role,
      ...(data.role === "store_manager" ? { store_id: data.store_id } : {}),
    }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as UserCreated;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callerRole: string;
}

export function CreateUserDialog({ open, onOpenChange, callerRole }: Props) {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [created, setCreated] = useState<UserCreated | null>(null);

  const availableRoles =
    callerRole === "super_admin" ? SUPER_ADMIN_ROLES : TENANT_ADMIN_ROLES;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", role: availableRoles[0], store_id: "" },
  });

  const isStoreManager = watch("role") === "store_manager";
  const storesQuery = useQuery({
    queryKey: ["stores", "all-for-users"],
    queryFn: fetchStores,
    enabled: open && isStoreManager,
  });

  useEffect(() => {
    if (open && !created) {
      reset({ name: "", email: "", role: availableRoles[0], store_id: "" });
      setServerError(null);
    }
  }, [open, created, reset, availableRoles]);

  const mutation = useMutation({
    mutationFn: createUser,
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setCreated(user);
    },
    onError: (err: Error) => setServerError(err.message),
  });

  function close() {
    onOpenChange(false);
    setCreated(null);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent>
        {created ? (
          <CreatedSuccessView user={created} onClose={close} />
        ) : (
          <>
            <DialogTitle>Novo usuário</DialogTitle>
            <DialogDescription>
              O usuário receberá uma senha temporária (até o envio por email
              entrar). Você verá a senha após criar.
            </DialogDescription>

            <form
              onSubmit={handleSubmit((data) => mutation.mutate(data))}
              className="mt-4 flex flex-col gap-4"
              noValidate
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="user-email">Email</Label>
                <Input
                  id="user-email"
                  type="email"
                  autoComplete="off"
                  autoFocus
                  aria-invalid={errors.email ? true : undefined}
                  {...register("email")}
                />
                {errors.email ? (
                  <span role="alert" className="text-xs text-[var(--color-destructive)]">
                    {errors.email.message}
                  </span>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="user-name">Nome</Label>
                <Input id="user-name" {...register("name")} />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="user-role">Role</Label>
                <Select id="user-role" {...register("role")}>
                  {availableRoles.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </div>

              {isStoreManager ? (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="user-store">Loja</Label>
                  <Select
                    id="user-store"
                    aria-invalid={errors.store_id ? true : undefined}
                    disabled={storesQuery.isLoading || !!storesQuery.error}
                    {...register("store_id")}
                  >
                    <option value="">
                      {storesQuery.isLoading
                        ? "Carregando lojas…"
                        : storesQuery.error
                          ? "Não foi possível carregar as lojas"
                          : "Escolha a loja"}
                    </option>
                    {(storesQuery.data ?? []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                  {errors.store_id ? (
                    <span role="alert" className="text-xs text-[var(--color-destructive)]">
                      {errors.store_id.message}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {serverError ? (
                <div
                  role="alert"
                  className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
                >
                  {serverError}
                </div>
              ) : null}

              <div className="mt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={close}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Criando…" : "Criar"}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreatedSuccessView({
  user,
  onClose,
}: {
  user: UserCreated;
  onClose: () => void;
}) {
  return (
    <>
      <DialogTitle>Usuário criado</DialogTitle>
      <DialogDescription>
        Copie a senha temporária e envie ao usuário. Esta senha não será
        mostrada novamente.
      </DialogDescription>

      <div className="mt-4 space-y-3 rounded-md border border-[var(--color-border)] bg-[var(--color-secondary)] p-4">
        <div>
          <p className="text-xs text-[var(--color-muted-foreground)]">Email</p>
          <p className="font-mono text-sm">{user.email}</p>
        </div>
        <div>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Senha temporária
          </p>
          <p className="select-all font-mono text-base">
            {user.temporary_password}
          </p>
        </div>
        <div>
          <p className="text-xs text-[var(--color-muted-foreground)]">Role</p>
          <p className="text-sm">{user.role}</p>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() =>
            navigator.clipboard.writeText(user.temporary_password)
          }
        >
          Copiar senha
        </Button>
        <Button onClick={onClose}>Fechar</Button>
      </div>
    </>
  );
}
